import { useRecoilValue } from 'recoil';
import { isNil } from 'lodash';
import moment from 'moment';
import classNames from 'classnames';

import * as K from 'common/constants';
import ELEMENT from 'common/element';
import log from 'common/ScalyrLogger';
import { AccountData, AccountType } from 'common/graphqlTypes';

import { accountDataState } from 'components/account/account.state';
import { useTrialExpiredHandling } from 'components/other/MainNavBar/AccountBadge.hooks';

import './AccountBadge.scss';

/**
 * The account status badge on the nav bar. Account data is retrieved from Recoil state.
 *
 * @returns The account badge JSX element, or null if the account type does not have a badge to show.
 *
 */
const AccountBadge = (): JSX.Element | null => {
  const account = useRecoilValue(accountDataState) || ({} as AccountData);

  const isValidExpirationDate =
    !isNil(account.trialExpiresAt) && moment(account.trialExpiresAt, moment.ISO_8601).isValid();

  let expireDate;
  let daysLeftInTrial = 100;
  let isTrialExpired = false;
  if (isValidExpirationDate) {
    expireDate = moment(account.trialExpiresAt);
    daysLeftInTrial = expireDate.diff(moment().utc(), 'days');
    isTrialExpired = daysLeftInTrial <= 0;
  }

  useTrialExpiredHandling(account.type, isTrialExpired);

  if (!account.type) {
    // Most likely, account type hasn't been retrieved from the API yet
    return null;
  }

  if (account.type === K.PAID || account.type === K.UNKNOWN) {
    return null;
  }

  if ((account.type as AccountType | 'DEMO') === K.DEMO) {
    return (
      <div
        role="presentation"
        className="AccountBadge__Demo AccountBadge__Demo--animation"
        onClick={(): void => {
          window.open(K.LINK.SIGN_UP, '_self');
        }}
      >
        TRY <span className="Badge__SignUpText">YOUR</span> DATA
      </div>
    );
  }

  if (account.type === K.TRIAL) {
    if (!isValidExpirationDate) {
      // This rarely happens.
      log.error('Trial account expiration date is invalid.');
      return null;
    }

    const TrialAccountClass = classNames('AccountBadge__Trial', {
      'AccountBadge__Trial--default': daysLeftInTrial > 15,
      'AccountBadge__Trial--normal': daysLeftInTrial > 10 && daysLeftInTrial <= 15,
      'AccountBadge__Trial--soon': daysLeftInTrial > 5 && daysLeftInTrial <= 10,
      'AccountBadge__Trial--urgent': daysLeftInTrial >= 1 && daysLeftInTrial <= 5,
      'AccountBadge__Trial--expired': isTrialExpired,
    });

    return (
      <div
        role="presentation"
        className={TrialAccountClass}
        onClick={(): void => {
          window.open(K.ROUTE.PLAN, '_self');
        }}
        data-test-id={ELEMENT.navAccountBadge}
      >
        <span className="Badge__Header">— TRIAL VERSION —</span>
        <span className="Badge__Body">
          {isTrialExpired ? 'TRIAL EXPIRED' : `${daysLeftInTrial} ${daysLeftInTrial === 1 ? 'DAY' : 'DAYS'} LEFT`}
          <span> &middot; </span>
          <span className="Badge__SignUpText">SIGN UP</span>
        </span>
      </div>
    );
  }

  log.warn(`Account type ${account.type} is not handled.`);
  return null;
};

export default AccountBadge;
